import React, { useState, useMemo, useCallback, useEffect } from 'react';
import useEscapeKey from '../../../hooks/useEscapeKey';
import Modal from '../../../components/Modal';
import { API_BASE } from '../utils/constants';
import { formatDate, getRequestType, getStatusColor, getRequestTypeColor, sortArray } from '../utils/helpers';

export default function RequestsTab({ requests, upcomingRequests, pastRequests, setRequests, setUpcomingRequests, setPastRequests, policies, loading, user }) {
  const [requestsTab, setRequestsTab] = useState('upcoming');
  const [statusFilter, setStatusFilter] = useState('all');
  const [requestSortField, setRequestSortField] = useState('start_date');
  const [requestSortOrder, setRequestSortOrder] = useState('asc');
  
  // Modal state
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [employeeData, setEmployeeData] = useState({});
  const [formData, setFormData] = useState({
    employee: '',
    start_date: '',
    end_date: '',
    policy_id: '',
    reason: '',
    start_portion: 'full',
    end_portion: 'full'
  });

  // Fetch employees when modal opens
  useEffect(() => {
    const fetchEmployees = async () => {
      if (!showForm) return;
      
      try {
        const res = await fetch(`${API_BASE}/api/admin/employees`, {
          headers: {
            'Content-Type': 'application/json',
            'X-User-Email': user?.email || ''
          },
          credentials: 'include'
        });
        if (!res.ok) throw new Error(`Failed to fetch employees: ${res.status}`);
        const data = await res.json();
        
        // Store both the employee list and the full employee data
        setEmployees(Object.keys(data.employees || {}));
        setEmployeeData(data.employees || {});
      } catch (e) {
        console.error('Error fetching employees:', e);
      }
    };
    
    fetchEmployees();
  }, [showForm, user]);

  // Get available policies for the selected employee
  const availablePolicies = useMemo(() => {
    if (!formData.employee || !employeeData[formData.employee]) {
      return [];
    }
    
    const selectedEmployee = employeeData[formData.employee];
    const employeePolicyIds = selectedEmployee.policies || [];
    
    // Filter policies to only show those assigned to the selected employee
    return policies.filter(policy => {
      const policyId = policy.id || policy.name;
      return employeePolicyIds.includes(policyId);
    });
  }, [formData.employee, employeeData, policies]);

  // Reset policy selection when employee changes
  useEffect(() => {
    if (formData.employee && formData.policy_id) {
      // Check if the currently selected policy is still available for the new employee
      const isStillAvailable = availablePolicies.some(p => 
        (p.id || p.name) === formData.policy_id
      );
      
      if (!isStillAvailable) {
        setFormData(prev => ({ ...prev, policy_id: '' }));
      }
    }
  }, [formData.employee, availablePolicies, formData.policy_id]);

  // Handle escape key to close modal
  useEscapeKey(() => {
    if (showForm) {
      setShowForm(false);
    }
  });

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.employee || !formData.start_date || !formData.end_date || !formData.policy_id) {
      alert('Please fill in all required fields');
      return;
    }
    
    if (new Date(formData.end_date) < new Date(formData.start_date)) {
      alert('End date cannot be earlier than start date');
      return;
    }
    
    setSubmitting(true);
    
    try {
      const res = await fetch(`${API_BASE}/api/admin/pto_requests`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-User-Email': user?.email || ''
        },
        credentials: 'include',
        body: JSON.stringify({
          user_email: formData.employee,
          start_date: formData.start_date,
          end_date: formData.end_date,
          policy_id: formData.policy_id,
          reason: formData.reason,
          start_portion: formData.start_portion,
          end_portion: formData.end_portion
        })
      });

      const data = await res.json();

      if (res.ok && data.success) {
        // Add new time off entry to the lists
        const newRequest = data.request;
        if (newRequest) {
          // Determine if request is upcoming or past
          const startDate = new Date(newRequest.start_date);
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          
          if (startDate >= today) {
            setUpcomingRequests && setUpcomingRequests(prev => [newRequest, ...prev]);
          } else {
            setPastRequests && setPastRequests(prev => [newRequest, ...prev]);
          }
          
          // Also update the main requests list
          setRequests && setRequests(prev => [newRequest, ...prev]);
        }
        
        // Reset form and close modal
        setFormData({
          employee: '',
          start_date: '',
          end_date: '',
          policy_id: '',
          reason: '',
          start_portion: 'full',
          end_portion: 'full'
        });
        setShowForm(false);
      } else {
        throw new Error(data.error || 'Failed to add time off');
      }
    } catch (error) {
      console.error('Error adding time off:', error);
      alert(error.message || 'Error adding time off');
    } finally {
      setSubmitting(false);
    }
  };

  // Helper function to filter requests by status
  const filterByStatus = useCallback((requestsList) => {
    if (statusFilter === 'all') return requestsList;
    const normFilter = statusFilter.toLowerCase();
    return requestsList.filter(r => {
      const s = (r.status || '').toLowerCase();
      if (normFilter === 'rejected') return s === 'rejected' || s === 'denied';
      return s === normFilter;
    });
  }, [statusFilter]);

  // Get filtered and sorted requests for current tab
  const currentRequests = useMemo(() => {
    const sourceRequests = requestsTab === 'upcoming' ? upcomingRequests : pastRequests;
    const filtered = filterByStatus(sourceRequests);
    return sortArray(filtered, requestSortField, requestSortOrder, policies);
  }, [requestsTab, upcomingRequests, pastRequests, filterByStatus, requestSortField, requestSortOrder, policies]);

  // Handle header click to toggle sorting
  const handleSort = (field) => {
    if (field === requestSortField) {
      setRequestSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setRequestSortField(field);
      setRequestSortOrder('asc');
    }
  };

  // Handle approve/reject/delete actions
  const handleReqAction = async (id, action) => {
    // Require confirmation for delete actions
    if (action === 'delete') {
      const confirmation = window.prompt(
        'Type DELETE to permanently remove this time off request.'
      );
      if (confirmation !== 'DELETE') {
        return; // User cancelled or didn't type DELETE correctly
      }
    }

    try {
      const res = await fetch(`${API_BASE}/api/admin/pto_requests/${id}`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json', 
          'X-User-Email': user?.email || ''
        },
        credentials: 'include',
        body: JSON.stringify({ action }),
      });
      
      if (!res.ok) throw new Error('Failed to update request');
      const data = await res.json();
      
      if (action === 'delete') {
        // For delete actions, remove the request from all state arrays
        setRequests(prev => prev.filter(r => r.id !== id));
        setUpcomingRequests(prev => prev.filter(r => r.id !== id));
        setPastRequests(prev => prev.filter(r => r.id !== id));
      } else {
        // For approve/reject actions, update the request in all state arrays
        const updatedRequest = data.request;
        
        if (updatedRequest) {
          setRequests(prev => prev.map(r => r.id === id ? updatedRequest : r));
          setUpcomingRequests(prev => prev.map(r => r.id === id ? updatedRequest : r));
          setPastRequests(prev => prev.map(r => r.id === id ? updatedRequest : r));
        }
      }
    } catch (e) {
      alert(e.message);
    }
  };

  if (loading) {
    return <div style={{ padding: 24 }}>Loading requests...</div>;
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <h2 className="content-title" style={{ margin: 0 }}>Time Off Requests</h2>
        <button className="primary-button" onClick={() => setShowForm(true)}>
          + Add Time Off
        </button>
      </div>

      {/* Tab Navigation */}
      <div style={{ display: 'flex', borderBottom: '1px solid #e5e7eb', marginBottom: 16 }}>
        <button
          onClick={() => setRequestsTab('upcoming')}
          style={{
            padding: '12px 24px',
            border: 'none',
            background: 'none',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: '500',
            color: requestsTab === 'upcoming' ? 'var(--symbolica-black)' : 'var(--symbolica-muted)',
            borderBottom: requestsTab === 'upcoming' ? '2px solid var(--symbolica-black)' : '2px solid transparent'
          }}
        >
          Upcoming ({upcomingRequests.length})
        </button>
        <button
          onClick={() => setRequestsTab('past')}
          style={{
            padding: '12px 24px',
            border: 'none',
            background: 'none',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: '500',
            color: requestsTab === 'past' ? 'var(--symbolica-black)' : 'var(--symbolica-muted)',
            borderBottom: requestsTab === 'past' ? '2px solid var(--symbolica-black)' : '2px solid transparent'
          }}
        >
          Past ({pastRequests.length})
        </button>
      </div>

      {/* Filter Controls */}
      <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 16 }}>
        <select 
          value={statusFilter} 
          onChange={(e) => setStatusFilter(e.target.value)}
          style={{ padding: '4px 8px', borderRadius: 4, border: '1px solid #d1d5db' }}
        >
          <option value="all">All Statuses</option>
          <option value="pending">Pending</option>
          <option value="approved">Approved</option>
          <option value="rejected">Rejected</option>
        </select>
      </div>

      {/* Requests Table */}
      <div className="table-scroll">
        <table className="emp-table">
          <thead>
            <tr>
              <th style={{ cursor: 'pointer', padding: 8 }} onClick={() => handleSort('user_email')}>
                Employee {requestSortField === 'user_email' ? (requestSortOrder === 'asc' ? '↑' : '↓') : ''}
              </th>
              <th style={{ cursor: 'pointer', padding: 8 }} onClick={() => handleSort('start_date')}>
                Start Date {requestSortField === 'start_date' ? (requestSortOrder === 'asc' ? '↑' : '↓') : ''}
              </th>
              <th style={{ cursor: 'pointer', padding: 8 }} onClick={() => handleSort('end_date')}>
                End Date {requestSortField === 'end_date' ? (requestSortOrder === 'asc' ? '↑' : '↓') : ''}
              </th>
              <th style={{ padding: 8 }}>Days</th>
              <th style={{ cursor: 'pointer', padding: 8 }} onClick={() => handleSort('policy_id')}>
                Policy {requestSortField === 'policy_id' ? (requestSortOrder === 'asc' ? '↑' : '↓') : ''}
              </th>
              <th style={{ cursor: 'pointer', padding: 8 }} onClick={() => handleSort('status')}>
                Status {requestSortField === 'status' ? (requestSortOrder === 'asc' ? '↑' : '↓') : ''}
              </th>
              <th style={{ padding: 8 }}>Reason</th>
              <th style={{ padding: 8 }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {currentRequests.map(req => (
              <tr key={req.id}>
                <td style={{ padding: 8 }}>{req.user_email}</td>
                <td style={{ padding: 8 }}>{formatDate(req.start_date)}</td>
                <td style={{ padding: 8 }}>{formatDate(req.end_date)}</td>
                <td style={{ padding: 8 }}>{req.days}</td>
                <td style={{ padding: 8 }}>
                  <span style={{ 
                    ...getRequestTypeColor(getRequestType(req, policies)),
                    padding: '2px 6px',
                    borderRadius: 4,
                    fontSize: '12px'
                  }}>
                    {getRequestType(req, policies)}
                  </span>
                </td>
                <td style={{ padding: 8 }}>
                  <span style={{ 
                    ...getStatusColor(req.status),
                    padding: '2px 6px',
                    borderRadius: 4,
                    fontSize: '12px'
                  }}>
                    {req.status}
                  </span>
                </td>
                <td style={{ padding: 8 }}>{req.reason || 'No reason provided'}</td>
                <td style={{ padding: 8 }}>
                  <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                    {req.status === 'pending' && (
                      <>
                        <button 
                          onClick={() => handleReqAction(req.id, 'approve')}
                          style={{
                            background: '#059669',
                            color: 'white',
                            border: 'none',
                            padding: '4px 8px',
                            borderRadius: 4,
                            cursor: 'pointer',
                            fontSize: '12px'
                          }}
                        >
                          Approve
                        </button>
                        <button 
                          onClick={() => handleReqAction(req.id, 'reject')}
                          style={{
                            background: '#dc2626',
                            color: 'white',
                            border: 'none',
                            padding: '4px 8px',
                            borderRadius: 4,
                            cursor: 'pointer',
                            fontSize: '12px'
                          }}
                        >
                          Reject
                        </button>
                      </>
                    )}
                    <button 
                      onClick={() => handleReqAction(req.id, 'delete')}
                      style={{
                        background: 'var(--symbolica-muted)',
                        color: 'white',
                        border: 'none',
                        padding: '4px 8px',
                        borderRadius: 4,
                        cursor: 'pointer',
                        fontSize: '12px'
                      }}
                    >
                      Delete
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Add Time Off Modal */}
      {showForm && (
        <Modal isOpen={showForm} onClose={() => setShowForm(false)}>
          <h3 style={{ fontSize: '1.3rem', fontWeight: '600', marginBottom: '1.5rem', color: 'var(--symbolica-black)' }}>
            Add Time Off
          </h3>
          
          <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <div>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: 'var(--symbolica-black)', marginBottom: '4px' }}>
                Employee *
              </label>
              <select
                name="employee"
                value={formData.employee}
                onChange={handleInputChange}
                required
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px'
                }}
              >
                <option value="">Select an employee</option>
                {employees.map(email => (
                  <option key={email} value={email}>{email}</option>
                ))}
              </select>
            </div>

            <div>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: 'var(--symbolica-black)', marginBottom: '4px' }}>
                Policy *
              </label>
              <select
                name="policy_id"
                value={formData.policy_id}
                onChange={handleInputChange}
                required
                disabled={!formData.employee}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px',
                  backgroundColor: !formData.employee ? '#F6F4F0' : 'white'
                }}
              >
                <option value="">
                  {!formData.employee ? 'Select an employee first' : 'Select a policy'}
                </option>
                {availablePolicies.map(policy => (
                  <option key={policy.id || policy.name} value={policy.id || policy.name}>
                    {policy.name} ({policy.policy_type === 'sick' ? 'Sick Time' : 'PTO'})
                  </option>
                ))}
              </select>
              {formData.employee && availablePolicies.length === 0 && (
                <div style={{ fontSize: '12px', color: '#dc2626', marginTop: '4px' }}>
                  This employee has no assigned policies
                </div>
              )}
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: 'var(--symbolica-black)', marginBottom: '4px' }}>
                  Start Date *
                </label>
                <input
                  type="date"
                  name="start_date"
                  value={formData.start_date}
                  onChange={handleInputChange}
                  required
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '14px'
                  }}
                />
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: 'var(--symbolica-black)', marginBottom: '4px' }}>
                  End Date *
                </label>
                <input
                  type="date"
                  name="end_date"
                  value={formData.end_date}
                  onChange={handleInputChange}
                  required
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '14px'
                  }}
                />
              </div>
            </div>

            {/* Half-day controls */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: 'var(--symbolica-black)', marginBottom: '4px' }}>
                  Start Day Portion
                </label>
                <select
                  name="start_portion"
                  value={formData.start_portion}
                  onChange={handleInputChange}
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '14px'
                  }}
                >
                  <option value="full">Full day</option>
                  <option value="pm">Half day (PM)</option>
                </select>
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: 'var(--symbolica-black)', marginBottom: '4px' }}>
                  End Day Portion
                </label>
                <select
                  name="end_portion"
                  value={formData.end_portion}
                  onChange={handleInputChange}
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '14px'
                  }}
                >
                  <option value="full">Full day</option>
                  <option value="am">Half day (AM)</option>
                </select>
              </div>
            </div>

            <div>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: 'var(--symbolica-black)', marginBottom: '4px' }}>
                Reason (Optional)
              </label>
              <textarea
                name="reason"
                value={formData.reason}
                onChange={handleInputChange}
                rows="3"
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px',
                  resize: 'vertical'
                }}
                placeholder="Optional reason for time off..."
              />
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '1rem' }}>
              <button
                type="button"
                onClick={() => setShowForm(false)}
                style={{
                  background: '#F0EDE6',
                  color: 'var(--symbolica-black)',
                  border: 'none',
                  borderRadius: '6px',
                  padding: '0.75rem 1.5rem',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={submitting}
                style={{
                  background: submitting ? 'var(--symbolica-muted)' : 'var(--symbolica-black)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  padding: '0.75rem 1.5rem',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: submitting ? 'not-allowed' : 'pointer'
                }}
              >
                {submitting ? 'Adding...' : 'Add Time Off'}
              </button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
} 